import React, { useState, useEffect } from 'react';
import { Calendar, AlertCircle, Check, Database } from 'lucide-react';
import { ref, push, set, onValue, remove, update } from 'firebase/database';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { database, storage } from '../../firebase/config';
import { useIsMobile } from '../../hooks/useIsMobile';
import AddGigForm from './AddGigForm';
import GigsList from './GigsList';

export default function GigsTab({ user, bandId, isPremium }) {
  const [gigs, setGigs] = useState([]);
  const [venues, setVenues] = useState([]);
  const [festivals, setFestivals] = useState([]);

  // UI state
  const [expandedGig, setExpandedGig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [uploadProgress, setUploadProgress] = useState({});

  // Enrichment modal state
  const [showEnrichmentPrompt, setShowEnrichmentPrompt] = useState(false);
  const [enrichmentFields, setEnrichmentFields] = useState([]);
  const [pendingGigSave, setPendingGigSave] = useState(null);

  const isMobile = useIsMobile();

  // ── Data listeners ────────────────────────────────────────
  useEffect(() => {
    const venuesRef = ref(database, 'venues');
    const unsubscribe = onValue(venuesRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setVenues(Object.keys(data).map(key => ({ id: key, type: 'venue', ...data[key] })));
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const festivalsRef = ref(database, 'festivals');
    const unsubscribe = onValue(festivalsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setFestivals(Object.keys(data).map(key => ({ id: key, type: 'festival', ...data[key] })));
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user || !bandId) { setGigs([]); return; }
    const gigsRef = ref(database, `bands/${bandId}/gigs`);
    const unsubscribe = onValue(gigsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setGigs(Object.keys(data).map(key => ({ id: key, ...data[key] }))
          .sort((a, b) => new Date(a.date) - new Date(b.date)));
      } else {
        setGigs([]);
      }
    });
    return () => unsubscribe();
  }, [user, bandId]);

  // ── File helpers ──────────────────────────────────────────
  const handleFileUpload = async (file, path, metadata) => {
    if (!file || !user || !bandId) return null;
    try {
      const timestamp = Date.now();
      const filename = `bands/${bandId}/${path}/${timestamp}_${file.name}`;
      const fileRef = storageRef(storage, filename);
      const uploadTask = uploadBytesResumable(fileRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(prev => ({ ...prev, [path]: progress }));
          },
          (error) => { setError('Upload failed: ' + error.message); reject(error); },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              setUploadProgress(prev => ({ ...prev, [path]: 0 }));
              resolve({
                fileName: file.name, fileURL: downloadURL, storagePath: filename,
                uploadDate: new Date().toISOString(),
                fileSize: (file.size / 1024).toFixed(2),
                uploadedBy: user.uid,
                uploadedByName: user.displayName || user.email,
                ...metadata
              });
            } catch (urlError) { reject(urlError); }
          }
        );
      });
    } catch (err) {
      setError('Failed to upload file: ' + err.message);
      return null;
    }
  };

  const deleteFile = async (storagePath) => {
    if (storagePath) {
      const fileRef = storageRef(storage, storagePath);
      await deleteObject(fileRef);
    }
  };

  // ── Gig CRUD ──────────────────────────────────────────────
  const saveGig = async (gigData, selectedLocation, locationType, isNewLocation, enrichmentFields, contributeToDatabase = false) => {
    try {
      setLoading(true);
      const gigsRef = ref(database, `bands/${bandId}/gigs`);
      const newGigRef = push(gigsRef);
      await set(newGigRef, gigData);

      if (contributeToDatabase && selectedLocation) {
        const dbPath = locationType === 'venue' ? 'venues' : 'festivals';
        const updates = {};
        enrichmentFields.forEach(field => { updates[field.field] = field.value; });
        updates.lastContributedBy = user.uid;
        updates.lastContributedAt = new Date().toISOString();
        await update(ref(database, `${dbPath}/${selectedLocation.id}`), updates);
        setSuccessMessage(`Gig added and ${locationType} database updated!`);
      } else if (isNewLocation) {
        const dbPath = locationType === 'venue' ? 'venues' : 'festivals';
        const newLocationRef = push(ref(database, dbPath));
        const newLocationData = {
          name: gigData.venue, type: locationType,
          website: gigData.website || null, email: gigData.contactEmail || null,
          phone: gigData.contactPhone || null, contactPerson: gigData.contactPerson || null,
          address: gigData.address || null, city: gigData.city || null,
          region: gigData.region || null, genres: [],
          dataSource: 'user-contributed', contributedBy: user.uid,
          contributedByBand: bandId, createdAt: new Date().toISOString()
        };
        if (locationType === 'venue') {
          newLocationData.capacity = gigData.capacity || null;
          newLocationData.genreConfidence = 'low';
        } else {
          newLocationData.estimatedVisitors = gigData.capacity || null;
          newLocationData.period = gigData.period || null;
        }
        await set(newLocationRef, newLocationData);
        setSuccessMessage(`Gig added and new ${locationType} contributed to database!`);
      } else {
        setSuccessMessage('Gig added successfully!');
      }
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      setError('Failed to add gig: ' + err.message);
    } finally {
      setLoading(false);
      setShowEnrichmentPrompt(false);
      setPendingGigSave(null);
    }
  };

  const handleAddGig = async (gigData, selectedLocation, locationType, isNewLocation, enrichmentOpportunities) => {
    if (enrichmentOpportunities.length > 0 && selectedLocation) {
      setEnrichmentFields(enrichmentOpportunities);
      setPendingGigSave({ gigData, selectedLocation, locationType, isNewLocation });
      setShowEnrichmentPrompt(true);
      return;
    }
    await saveGig(gigData, selectedLocation, locationType, isNewLocation, [], false);
  };

  const handleSetlistUpload = async (gigId, file) => {
    if (!file || !user || !bandId) return;
    setLoading(true);
    const fileData = await handleFileUpload(file, `setlists/${gigId}`, {});
    if (fileData) {
      await update(ref(database, `bands/${bandId}/gigs/${gigId}`), { setlist: fileData });
    }
    setLoading(false);
  };

  const handlePhotoUpload = async (gigId, file) => {
    if (!file || !user || !bandId) return;
    setLoading(true);
    const fileData = await handleFileUpload(file, `photos/${gigId}`, {});
    if (fileData) {
      const photoRef = ref(database, `bands/${bandId}/gigs/${gigId}/photos`);
      const newPhotoRef = push(photoRef);
      await set(newPhotoRef, fileData);
    }
    setLoading(false);
  };

  const deleteGig = async (gigId) => {
    if (!user || !bandId) return;
    try {
      const gig = gigs.find(g => g.id === gigId);
      if (gig) {
        if (gig.setlist) await deleteFile(gig.setlist.storagePath);
        if (gig.photos) {
          const photosArray = Object.keys(gig.photos).map(key => gig.photos[key]);
          for (const photo of photosArray) { await deleteFile(photo.storagePath); }
        }
      }
      await remove(ref(database, `bands/${bandId}/gigs/${gigId}`));
      setExpandedGig(null);
      setError('');
    } catch (err) {
      setError('Failed to delete gig: ' + err.message);
    }
  };

  const deleteGigPhoto = async (gigId, photoId, storagePath) => {
    await deleteFile(storagePath);
    await remove(ref(database, `bands/${bandId}/gigs/${gigId}/photos/${photoId}`));
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-900/50 rounded-card p-4 flex items-center gap-3">
          <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
          <p className="text-green-100 text-sm">{successMessage}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/50 rounded-card p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1"><p className="text-red-100 text-sm">{error}</p></div>
          <button onClick={() => setError('')} className="text-red-300 hover:text-red-200">×</button>
        </div>
      )}

      {/* Enrichment Prompt Modal */}
      {showEnrichmentPrompt && pendingGigSave && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="card-3d border border-surface-border-light rounded-card p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-6 h-6 text-brand" />
              <h3 className="text-lg font-semibold">Help Improve the Database</h3>
            </div>
            <p className="text-sm text-text-muted mb-4">
              You've added information that could help other bands. Would you like to contribute these updates to the {pendingGigSave.locationType} database?
            </p>
            <div className="border border-surface-border-light rounded-card-btn p-3 mb-4 space-y-2">
              {enrichmentFields.map((field, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-text-subtle">{field.label}:</span>
                  <span className="text-white font-medium truncate ml-2">{field.value}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => saveGig(
                  pendingGigSave.gigData, pendingGigSave.selectedLocation,
                  pendingGigSave.locationType, pendingGigSave.isNewLocation,
                  enrichmentFields, true
                )}
                disabled={loading}
                className="flex-1 bg-brand hover:bg-brand-hover disabled:opacity-disabled transition px-4 py-2 font-semibold text-sm flex items-center justify-center gap-2 rounded-pill"
              >
                <Database className="w-4 h-4" />
                Contribute & Save
              </button>
              <button
                onClick={() => saveGig(
                  pendingGigSave.gigData, pendingGigSave.selectedLocation,
                  pendingGigSave.locationType, pendingGigSave.isNewLocation,
                  enrichmentFields, false
                )}
                disabled={loading}
                className="flex-1 bg-white/10 hover:bg-white/20 transition px-4 py-2 font-semibold text-sm rounded-pill"
              >
                Just Save Gig
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Gig Form */}
      <AddGigForm
        user={user}
        bandId={bandId}
        venues={venues}
        festivals={festivals}
        loading={loading}
        onAddGig={handleAddGig}
      />

      {/* Gigs List */}
      <GigsList
        gigs={gigs}
        isPremium={isPremium}
        expandedGig={expandedGig}
        onToggleExpand={(gigId) => setExpandedGig(expandedGig === gigId ? null : gigId)}
        onDeleteGig={deleteGig}
        onSetlistUpload={handleSetlistUpload}
        onPhotoUpload={handlePhotoUpload}
        onDeletePhoto={deleteGigPhoto}
        loading={loading}
        isMobile={isMobile}
      />
    </div>
  );
}
